import React, { useState, useRef, useEffect } from 'react';
import { Button, Box, Typography, CircularProgress, Paper, Alert, Link } from '@mui/material'; // 添加了Link组件
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import axios from 'axios'; // 导入axios

// 配置后端API地址
const API_BASE_URL = 'http://localhost:5001'; 

function VideoUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [inputFileUrl, setInputFileUrl] = useState(null);
  const [outputVideoUrl, setOutputVideoUrl] = useState(null);
  const [statsReportUrl, setStatsReportUrl] = useState(null); 
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState('');
  const [detectionMessage, setDetectionMessage] = useState('');

  const fileInputRef = useRef(null);
  const inputVideoRef = useRef(null);

  // 清理对象URL
  useEffect(() => {
    const inputUrl = inputFileUrl;
    const outputUrl = outputVideoUrl; // 输出URL来自后端，无需清理blob
    return () => {
      if (inputUrl && inputUrl.startsWith('blob:')) {
        console.log("撤销输入blob URL:", inputUrl);
        URL.revokeObjectURL(inputUrl);
      }
      // 如果outputVideoUrl是标准HTTP URL则无需处理
    };
  }, [inputFileUrl, outputVideoUrl]);

  const handleFileChange = (event) => {
    console.log("开始处理文件变更");
    const file = event.target.files[0];
    console.log("选中的文件:", file);

    // 清除之前的状态
    setSelectedFile(null);
    setInputFileUrl(prevUrl => {
        if (prevUrl && prevUrl.startsWith('blob:')) {
            URL.revokeObjectURL(prevUrl);
        }
        return null;
    });
    setOutputVideoUrl(null); // 清除输出URL
    setStatsReportUrl(null); // 清除统计URL
    setError('');
    setDetectionMessage('');

    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
      try {
          const url = URL.createObjectURL(file);
          console.log("创建输入blob URL:", url);
          setInputFileUrl(url);
      } catch (e) {
          console.error("创建对象URL错误:", e);
          setError("无法创建视频预览链接。");
          setSelectedFile(null);
      }
    } else {
      if(file){
          setError('请选择有效的视频文件');
      } else {
          setError('');
      }
      setSelectedFile(null);
      setInputFileUrl(null);
    }

     if (fileInputRef.current) {
        fileInputRef.current.value = '';
        console.log("文件输入值已清空");
     }
     console.log("文件变更处理完成");
  };

  const handleDetectClick = async () => {
    console.log("开始检测点击 - 调用后端API");
    if (!selectedFile || !inputFileUrl) {
      setError('请先选择视频文件并等待预览加载');
      console.error("检测中止：没有文件或输入URL");
      return;
    }

    // 清除之前的结果
    setOutputVideoUrl(null);
    setStatsReportUrl(null);
    setIsDetecting(true);
    setError('');
    setDetectionMessage('正在上传并处理视频，请稍候...');

    // 调用后端API
    const formData = new FormData();
    formData.append('video', selectedFile); // 'video'需要与后端参数名匹配

    try {
      console.log('发送视频到API:', `${API_BASE_URL}/api/process_video`);
      const response = await axios.post(`${API_BASE_URL}/api/process_video`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('API响应:', response.data);

      if (response.data && response.data.outputVideoUrl && response.data.statsUrl) {
        setOutputVideoUrl(response.data.outputVideoUrl);
        setStatsReportUrl(response.data.statsUrl); // 存储统计URL
        setDetectionMessage(`视频处理完成: ${selectedFile.name}`);
      } else {
         throw new Error(response.data.error || 'API返回无效数据');
      }

    } catch (err) {
      console.error('API调用失败:', err);
      let errorMsg = '分析失败: ';
      if (err.response) {
        // 服务器返回了非2xx的状态码
        errorMsg += `服务器错误 ${err.response.status} - ${err.response.data?.error || err.message}`;
        console.error('错误数据:', err.response.data);
        console.error('错误状态码:', err.response.status);
      } else if (err.request) {
        // 请求已发送但无响应（如网络错误、后端服务不可用）
        errorMsg += '无法连接分析服务器，请检查网络或联系管理员';
        console.error('错误请求:', err.request);
      } else {
        // 请求配置阶段出错
        errorMsg += err.message;
      }
      setError(errorMsg);
      setOutputVideoUrl(null);
      setStatsReportUrl(null);
    } finally {
      setIsDetecting(false); // 结束检测状态
      console.log("检测点击处理完成");
    }
    // API调用结束
  };

  const triggerFileInput = () => {
    console.log("触发文件输入点击");
    fileInputRef.current.click();
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        上传视频进行疲劳分析
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {detectionMessage && !error && <Alert severity={isDetecting ? "info" : "success"} sx={{ mb: 2 }}>{detectionMessage}</Alert>}

      <Box sx={{ mb: 3 }}>
        <input
          type="file"
          accept="video/*"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="video-upload-input-fatigue"
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={triggerFileInput}
          disabled={isDetecting}
        >
          选择视频文件
        </Button>
        {selectedFile && (
          <Typography variant="body1" sx={{ display: 'inline', ml: 2 }}>
            已选择: {selectedFile.name}
          </Typography>
        )}
      </Box>

      {inputFileUrl && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>输入视频预览:</Typography>
          <video
            ref={inputVideoRef}
            src={inputFileUrl}
            controls
            width="100%"
            style={{ maxHeight: '400px', backgroundColor: '#f0f0f0' }}
            onError={(e) => { console.error("输入视频加载错误", e); setError("无法加载输入视频预览。"); }}
          />
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        startIcon={<PlayCircleOutlineIcon />}
        onClick={handleDetectClick}
        disabled={!selectedFile || isDetecting || !inputFileUrl}
        sx={{ mb: 2 }}
      >
        {isDetecting ? <CircularProgress size={24} color="inherit" /> : '开始疲劳分析'}
      </Button>

      {isDetecting && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          {detectionMessage || "正在分析中..."} {/* 显示动态消息 */}
        </Typography>
      )}

      {/* 显示输出视频 */}
      {outputVideoUrl && !isDetecting && (
         <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>疲劳分析结果视频:</Typography>
          <video
            key={outputVideoUrl} // key用于强制重新渲染
            src={outputVideoUrl}
            controls
            width="100%"
            style={{ maxHeight: '400px', backgroundColor: '#e0e0e0' }}
            onError={(e) => { console.error("输出视频加载错误", e); setError("无法加载分析结果视频。请检查后端服务是否正确返回视频。"); }}
          />
        </Box>
      )}

      {/* 显示统计报告链接 */}
      {statsReportUrl && !isDetecting && (
        <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
                <Link href={statsReportUrl} target="_blank" rel="noopener noreferrer">
                    查看疲劳统计报告
                </Link>
            </Typography>
        </Box>
      )}

    </Paper>
  );
}

export default VideoUpload;